import React, { useState, useEffect } from 'react';
import {
  Wallet,
  Send,
  Download,
  Upload,
  Shield,
  Key,
  Eye,
  EyeOff,
  Copy,
  CheckCircle,
  AlertTriangle,
  Zap,
  TrendingUp,
  Users,
  Settings
} from 'lucide-react';

interface FoundationWallet {
  id: string;
  name: string;
  address: string;
  type: 'foundation' | 'airdrop' | 'staking' | 'node_payment' | 'emergency';
  vttnBalance: number;
  usdtBalance: number;
  totalBalance: number;
  isActive: boolean;
  lastActivity: string;
  transactionCount: number;
}

interface WalletTransaction {
  id: string;
  hash: string;
  type: 'send' | 'receive' | 'airdrop' | 'commission' | 'staking_reward';
  amount: number;
  token: string;
  fromAddress: string;
  toAddress: string;
  timestamp: string;
  status: 'success' | 'pending' | 'failed';
  description: string;
}

interface WalletStats {
  totalBalance: number;
  totalAirdrops: number;
  totalCommissions: number;
  totalStakingRewards: number;
  activeWallets: number;
}

const FoundationWalletPage: React.FC = () => {
  const [wallets, setWallets] = useState<FoundationWallet[]>([]);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [stats, setStats] = useState<WalletStats>({
    totalBalance: 0,
    totalAirdrops: 0,
    totalCommissions: 0,
    totalStakingRewards: 0,
    activeWallets: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedWallet, setSelectedWallet] = useState<FoundationWallet | null>(null);
  const [showPrivateKeys, setShowPrivateKeys] = useState<{[key: string]: boolean}>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    type: 'foundation' as FoundationWallet['type'],
    description: '',
    autoGenerate: true,
    privateKey: '',
    address: '',
    previewAddress: ''
  });

  useEffect(() => {
    loadWalletData();
    const interval = setInterval(loadWalletData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      
      // 실제 API 호출
      const [walletsRes, transactionsRes, statsRes] = await Promise.all([
        fetch('http://localhost:3600/api/admin/foundation/wallets'),
        fetch('http://localhost:3600/api/admin/foundation/transactions'),
        fetch('http://localhost:3600/api/admin/foundation/stats')
      ]);
      
      // Mock data for development
      const mockWallets: FoundationWallet[] = [
        {
          id: 'foundation-main',
          name: 'Foundation Main Wallet',
          address: '0xVOLTRONFOUNDATION000000000000000A888888',
          type: 'foundation',
          vttnBalance: 500000000,
          usdtBalance: 500000000,
          totalBalance: 1000000000,
          isActive: true,
          lastActivity: new Date(Date.now() - 300000).toISOString(),
          transactionCount: 1250
        },
        {
          id: 'airdrop-wallet',
          name: 'Airdrop Distribution Wallet',
          address: '0xAIRDROP1234567890123456789012345678901',
          type: 'airdrop',
          vttnBalance: 100000000,
          usdtBalance: 50000000,
          totalBalance: 150000000,
          isActive: true,
          lastActivity: new Date(Date.now() - 600000).toISOString(),
          transactionCount: 5420
        },
        {
          id: 'staking-pool',
          name: 'Staking Pool Wallet',
          address: '0xSTAKING567890123456789012345678901234',
          type: 'staking',
          vttnBalance: 200000000,
          usdtBalance: 100000000,
          totalBalance: 300000000,
          isActive: true,
          lastActivity: new Date(Date.now() - 900000).toISOString(),
          transactionCount: 890
        },
        {
          id: 'node-payment',
          name: 'Node Subscription Payment',
          address: '0xNODEPAY890123456789012345678901234567',
          type: 'node_payment',
          vttnBalance: 50000000,
          usdtBalance: 200000000,
          totalBalance: 250000000,
          isActive: true,
          lastActivity: new Date(Date.now() - 1200000).toISOString(),
          transactionCount: 340
        },
        {
          id: 'emergency-fund',
          name: 'Emergency Reserve Fund',
          address: '0xEMERGENCY123456789012345678901234567',
          type: 'emergency',
          vttnBalance: 1000000000,
          usdtBalance: 1000000000,
          totalBalance: 2000000000,
          isActive: true,
          lastActivity: new Date(Date.now() - 86400000).toISOString(),
          transactionCount: 45
        }
      ];
      
      const mockTransactions: WalletTransaction[] = [
        {
          id: 'tx-1',
          hash: '0x1234567890abcdef1234567890abcdef12345678',
          type: 'airdrop',
          amount: 100,
          token: 'VTTN',
          fromAddress: '0xAIRDROP1234567890123456789012345678901',
          toAddress: '0x1234567890123456789012345678901234444444',
          timestamp: new Date(Date.now() - 300000).toISOString(),
          status: 'success',
          description: 'Welcome airdrop to new user'
        },
        {
          id: 'tx-2',
          hash: '0xabcdef1234567890abcdef1234567890abcdef12',
          type: 'commission',
          amount: 2500,
          token: 'VTTN',
          fromAddress: '0xVOLTRONFOUNDATION000000000000000A888888',
          toAddress: '0x1234567890123456789012345678901234111111',
          timestamp: new Date(Date.now() - 600000).toISOString(),
          status: 'success',
          description: 'Staking commission payment'
        }
      ];
      
      const mockStats: WalletStats = {
        totalBalance: 3700000000,
        totalAirdrops: 542000,
        totalCommissions: 2168000,
        totalStakingRewards: 1890000,
        activeWallets: 5
      };
      
      setWallets(mockWallets);
      setTransactions(mockTransactions);
      setStats(mockStats);
      
    } catch (error) {
      console.error('Failed to load wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendTokens = async (sendData: any) => {
    try {
      const response = await fetch('http://localhost:3600/api/admin/foundation/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sendData)
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadWalletData();
          alert('Tokens sent successfully!');
        }
      }
    } catch (error) {
      console.error('Failed to send tokens:', error);
      alert('Failed to send tokens.');
    }
  };

  const createNewWallet = async (formData: typeof createForm) => {
    try {
      // 지갑 생성 로직
      let newWallet: FoundationWallet;
      
      if (formData.autoGenerate) {
         // 자동 생성 - 미리보기 주소 사용
         const generatedAddress = formData.previewAddress || `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`;
         newWallet = {
           id: Date.now().toString(),
           name: formData.name,
           address: generatedAddress,
           type: formData.type,
           vttnBalance: 0,
           usdtBalance: 0,
           totalBalance: 0,
           isActive: true,
           lastActivity: new Date().toISOString(),
           transactionCount: 0
         };
      } else {
        // 수동 입력
        newWallet = {
          id: Date.now().toString(),
          name: formData.name,
          address: formData.address,
          type: formData.type,
          vttnBalance: 0,
          usdtBalance: 0,
          totalBalance: 0,
          isActive: true,
          lastActivity: new Date().toISOString(),
          transactionCount: 0
        };
      }
      
      setWallets([...wallets, newWallet]);
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        type: 'foundation',
        description: '',
        autoGenerate: true,
        privateKey: '',
        address: '',
        previewAddress: ''
      });
      
      alert(`새 재단 지갑이 성공적으로 생성되었습니다!\n주소: ${newWallet.address}`);
    } catch (error) {
      console.error('지갑 생성 실패:', error);
      alert('지갑 생성에 실패했습니다.');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const togglePrivateKey = (walletId: string) => {
    setShowPrivateKeys(prev => ({
      ...prev,
      [walletId]: !prev[walletId]
    }));
  };

  const getWalletTypeColor = (type: string) => {
    switch (type) {
      case 'foundation': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'airdrop': return 'text-green-600 bg-green-50 border-green-200';
      case 'staking': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'node_payment': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'emergency': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getWalletTypeIcon = (type: string) => {
    switch (type) {
      case 'foundation': return <Shield className="w-4 h-4" />;
      case 'airdrop': return <Zap className="w-4 h-4" />;
      case 'staking': return <TrendingUp className="w-4 h-4" />;
      case 'node_payment': return <Settings className="w-4 h-4" />;
      case 'emergency': return <AlertTriangle className="w-4 h-4" />;
      default: return <Wallet className="w-4 h-4" />;
    }
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'send': return 'text-red-600 bg-red-50';
      case 'receive': return 'text-green-600 bg-green-50';
      case 'airdrop': return 'text-blue-600 bg-blue-50';
      case 'commission': return 'text-purple-600 bg-purple-50';
      case 'staking_reward': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Foundation Wallet Management</h1>
          <p className="text-gray-600 mt-2">Manage foundation wallets and token distribution</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.open('/admin/token-transfer', '_blank')}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Send className="w-4 h-4" />
            <span>Send Tokens</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Wallet className="w-4 h-4" />
            <span>New Wallet</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalBalance / 1000000).toFixed(1)}M</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Wallet className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Airdrops</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalAirdrops / 1000).toFixed(0)}K</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Zap className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Commissions</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalCommissions / 1000).toFixed(0)}K</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Staking Rewards</p>
              <p className="text-2xl font-bold text-gray-900">${(stats.totalStakingRewards / 1000).toFixed(0)}K</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Wallets</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeWallets}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-lg">
              <Users className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Wallets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {wallets.map((wallet) => (
          <div key={wallet.id} className="card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${getWalletTypeColor(wallet.type)}`}>
                  {getWalletTypeIcon(wallet.type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{wallet.name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getWalletTypeColor(wallet.type)}`}>
                    {wallet.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${wallet.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Address:</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-mono text-gray-900">
                    {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(wallet.address)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">VTTN Balance:</span>
                <span className="text-sm font-medium text-gray-900">
                  {wallet.vttnBalance.toLocaleString()} VTTN
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">USDT Balance:</span>
                <span className="text-sm font-medium text-gray-900">
                  {wallet.usdtBalance.toLocaleString()} USDT
                </span>
              </div>
              
              <div className="flex items-center justify-between border-t pt-3">
                <span className="text-sm font-medium text-gray-600">Total Value:</span>
                <span className="text-lg font-bold text-gray-900">
                  ${wallet.totalBalance.toLocaleString()}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Transactions:</span>
                <span className="text-sm text-gray-900">{wallet.transactionCount}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last Activity:</span>
                <span className="text-sm text-gray-900">
                  {new Date(wallet.lastActivity).toLocaleDateString()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <button
                onClick={() => togglePrivateKey(wallet.id)}
                className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {showPrivateKeys[wallet.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span>Private Key</span>
              </button>
              
              <button
                onClick={() => {
                  // Store wallet info in localStorage to activate in wallet page
                  localStorage.setItem('selectedWalletAddress', wallet.address);
                  localStorage.setItem('selectedWalletName', wallet.name);
                  localStorage.setItem('selectedWalletType', wallet.type);
                  // Navigate to wallet page
                  window.open('/wallet', '_blank');
                }}
                className="text-sm text-blue-600 hover:text-blue-900"
              >
                View Details
              </button>
            </div>
            
            {showPrivateKeys[wallet.id] && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Key className="w-4 h-4 text-yellow-600" />
                  <span className="text-xs font-mono text-yellow-800">
                    0x{Math.random().toString(16).substring(2, 66)}
                  </span>
                  <button
                    onClick={() => copyToClipboard('0x' + Math.random().toString(16).substring(2, 66))}
                    className="text-yellow-600 hover:text-yellow-800"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Recent Transactions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Transactions</h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  From/To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{tx.description}</div>
                      <div className="text-sm text-gray-500 font-mono">
                        {tx.hash.slice(0, 10)}...{tx.hash.slice(-8)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${getTransactionTypeColor(tx.type)}`}>
                      {tx.type.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {tx.amount.toLocaleString()} {tx.token}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>From: {tx.fromAddress.slice(0, 10)}...{tx.fromAddress.slice(-8)}</div>
                      <div>To: {tx.toAddress.slice(0, 10)}...{tx.toAddress.slice(-8)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium ${
                      tx.status === 'success' ? 'text-green-600 bg-green-50 border border-green-200' :
                      tx.status === 'pending' ? 'text-yellow-600 bg-yellow-50 border border-yellow-200' :
                      'text-red-600 bg-red-50 border border-red-200'
                    }`}>
                      {tx.status === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                      {tx.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(tx.timestamp).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


      {/* Create Wallet Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Create New Foundation Wallet</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Wallet Name</label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      placeholder="e.g., Marketing Wallet"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const suggestions = [
                          'Marketing Wallet',
                          'Development Fund',
                          'Community Rewards',
                          'Partnership Pool',
                          'Reserve Fund',
                          'Team Allocation',
                          'Advisor Tokens',
                          'Ecosystem Growth',
                          'Treasury Wallet',
                          'Liquidity Pool'
                        ];
                        const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
                        setCreateForm({ ...createForm, name: randomSuggestion });
                      }}
                      className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      Suggest
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Wallet Type</label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm({ ...createForm, type: e.target.value as FoundationWallet['type'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="foundation">Foundation (Main)</option>
                    <option value="airdrop">Airdrop (Distribution)</option>
                    <option value="staking">Staking (Pool)</option>
                    <option value="node_payment">Node Payment (Rewards)</option>
                    <option value="emergency">Emergency (Reserve)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Describe the wallet purpose and management policy"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      id="autoGenerate"
                      checked={createForm.autoGenerate}
                      onChange={(e) => {
                        const newAutoGenerate = e.target.checked;
                        let newPreviewAddress = '';
                        if (newAutoGenerate) {
                          newPreviewAddress = `0x${Math.random().toString(16).substring(2, 42).padStart(40, '0')}`;
                        }
                        setCreateForm({ 
                          ...createForm, 
                          autoGenerate: newAutoGenerate,
                          previewAddress: newPreviewAddress
                        });
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="autoGenerate" className="text-sm font-medium text-gray-700">
                      Auto-generate key pair (Recommended)
                    </label>
                  </div>
                  
                  {createForm.autoGenerate && createForm.previewAddress && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium text-blue-700 mb-1">Preview Wallet Address</label>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm text-blue-800 break-all">{createForm.previewAddress}</span>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(createForm.previewAddress)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {!createForm.autoGenerate && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Private Key</label>
                        <input
                          type="password"
                          value={createForm.privateKey}
                          onChange={(e) => setCreateForm({ ...createForm, privateKey: e.target.value })}
                          placeholder="0x..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Wallet Address</label>
                        <input
                          type="text"
                          value={createForm.address}
                          onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                          placeholder="0x..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Security Notice</p>
                      <p>Store the generated private key in a secure location. Recovery is impossible if lost.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({
                      name: '',
                      type: 'foundation',
                      description: '',
                      autoGenerate: true,
                      privateKey: '',
                      address: '',
                      previewAddress: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await createNewWallet(createForm);
                    } catch (error) {
                      console.error('Failed to create wallet:', error);
                      alert('Failed to create wallet.');
                    }
                  }}
                  disabled={!createForm.name || (!createForm.autoGenerate && (!createForm.privateKey || !createForm.address))}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Create Wallet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoundationWalletPage;